import {
  Crosshair,
  Globe,
  LocateFixed,
  MousePointer2,
  Move3d,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import { IDENTITY_GROUP_TRANSFORM } from "../../app/storeTypes.ts";
import {
  storeKeyToEntityId,
  updateEntityTransform,
  useComponent,
  useEntity,
} from "../../ecs/index.ts";
import type { PivotReference } from "../../modeling/types.ts";
import { Button } from "../Button.tsx";
import { PivotIcon } from "../icons3d.tsx";
import { Popover } from "../Popover.tsx";
import { CollapsiblePanel, SliderRow, SubsectionLabel } from "../shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_SURFACE,
  COLOR_TEXT_SECONDARY,
} from "../tokens.ts";

const PIVOT_OPTIONS: {
  value: PivotReference;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "median", label: "Median", icon: <Crosshair size={13} /> },
  { value: "world", label: "Origin", icon: <Globe size={13} /> },
  {
    value: "active",
    label: "Active",
    icon: <MousePointer2 size={13} />,
  },
  {
    value: "individual",
    label: "Individual Origins",
    icon: <Move3d size={13} />,
  },
  { value: "cursor", label: "3D Cursor", icon: <LocateFixed size={13} /> },
];

export function PivotSection({
  objectId,
  bare,
}: {
  objectId?: string;
  bare?: boolean;
}) {
  const pivotReference = useStore((s) => s.pivotReference);
  const setPivotReference = useStore((s) => s.setPivotReference);
  const pushUndo = useStore((s) => s.pushUndo);
  const editMode = useStore((s) => s.editMode);
  const hasSelection =
    editMode != null &&
    editMode.objectId === objectId &&
    editMode.selectedVertices.size > 0;
  const [open, setOpen] = useState(false);
  const pivotBtnRef = useRef<HTMLButtonElement>(null);

  const current = PIVOT_OPTIONS.find((o) => o.value === pivotReference)!;

  const pivotContent = (
    <>
      {/* Pivot reference picker */}
      <SubsectionLabel>Pivot Reference</SubsectionLabel>
      <div style={{ marginTop: 4 }}>
        <Button
          ref={pivotBtnRef}
          onClick={() => setOpen((p) => !p)}
          variant="secondary"
          fullWidth
          leftIcon={current.icon}
          style={{
            background: open ? COLOR_ACTIVE_BG_SUBTLE : COLOR_SURFACE,
            color: COLOR_TEXT_SECONDARY,
            justifyContent: "flex-start",
          }}
        >
          {current.label}
        </Button>

        <Popover
          anchorRef={pivotBtnRef}
          open={open}
          onClose={() => setOpen(false)}
          style={{ minWidth: 160 }}
        >
          {PIVOT_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              data-popover-item
              onClick={() => {
                setPivotReference(opt.value);
                setOpen(false);
              }}
              variant="ghost"
              size="sm"
              fullWidth
              leftIcon={opt.icon}
              style={{
                padding: "8px 12px",
                background:
                  pivotReference === opt.value
                    ? COLOR_ACTIVE_BG
                    : "transparent",
                color:
                  pivotReference === opt.value
                    ? COLOR_ACCENT
                    : COLOR_TEXT_SECONDARY,
                justifyContent: "flex-start",
              }}
            >
              {opt.label}
            </Button>
          ))}
        </Popover>
      </div>

      {/* Set Origin — only for scene objects */}
      {objectId && (
        <div style={{ marginTop: 10 }}>
          <SubsectionLabel>Set Origin</SubsectionLabel>
          <div
            style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}
          >
            <Button
              title="Move origin to the center of the mesh geometry"
              onClick={() => {
                pushUndo();
                useStore.getState().setOriginToGeometry(objectId);
              }}
              variant="secondary"
              size="xs"
            >
              Geometry
            </Button>
            <Button
              title="Move origin to the world origin (0, 0, 0)"
              onClick={() => {
                pushUndo();
                useStore.getState().setOriginToWorld(objectId);
              }}
              variant="secondary"
              size="xs"
            >
              World Origin
            </Button>
            <Button
              title="Move origin to the 3D cursor position"
              onClick={() => {
                const s = useStore.getState();
                pushUndo();
                const cursorPos: [number, number, number] = [
                  s.cursor3D.position[0],
                  s.cursor3D.position[1],
                  s.cursor3D.position[2],
                ];
                s.setOriginToPosition(objectId, cursorPos);
              }}
              variant="secondary"
              size="xs"
            >
              3D Cursor
            </Button>
            <Button
              title={
                hasSelection
                  ? "Move origin to the center of the selected vertices"
                  : "Select vertices in edit mode first"
              }
              disabled={!hasSelection}
              onClick={() => {
                pushUndo();
                useStore.getState().setOriginToSelection(objectId);
              }}
              variant="secondary"
              size="xs"
            >
              Selection
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (bare) return pivotContent;

  return (
    <CollapsiblePanel
      id="pivot"
      title="Pivot & Origin"
      icon={<PivotIcon size={11} />}
      summary={current.label}
      defaultCollapsed
    >
      {pivotContent}
    </CollapsiblePanel>
  );
}

const AXIS_LABELS = ["X", "Y", "Z"] as const;

export function TransformSection({
  objectId,
  bare,
}: {
  objectId: string;
  bare?: boolean;
}) {
  const eid = storeKeyToEntityId(objectId);
  const entity = useEntity(eid);
  const transform = useComponent(eid, "transform");
  const pushUndo = useStore((s) => s.pushUndo);

  if (!entity || !transform) return null;

  const isBlend = entity.origin === "blend";
  const pos = transform.position as [number, number, number];
  const rot = transform.rotation as [number, number, number];
  const scl = transform.scale as [number, number, number];

  const update = (
    newPos: [number, number, number],
    newRot: [number, number, number],
    newScl: [number, number, number],
  ) => {
    updateEntityTransform(eid, newPos, newRot, newScl);
  };

  const setPos = (axis: number, v: number) => {
    const p: [number, number, number] = [...pos];
    p[axis] = v;
    update(p, rot, scl);
  };
  const setRot = (axis: number, deg: number) => {
    const r: [number, number, number] = [...rot];
    r[axis] = (deg * Math.PI) / 180;
    update(pos, r, scl);
  };
  const setScl = (axis: number, v: number) => {
    const s: [number, number, number] = [...scl];
    s[axis] = v;
    update(pos, rot, s);
  };

  // Compact summary for collapsed state
  const summary = `${pos[0].toFixed(1)}, ${pos[1].toFixed(1)}, ${pos[2].toFixed(1)}`;

  const transformContent = (
    <>
      {/* Position */}
      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Position</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => (
          <SliderRow
            key={`pos-${axis}`}
            label={axis}
            labelWidth={16}
            value={Math.round(pos[i] * 100)}
            min={-1000}
            max={1000}
            step={1}
            displayValue={pos[i].toFixed(2)}
            onChange={(v) => setPos(i, v / 100)}
            defaultValue={0}
            parseValue={(raw) => {
              const n = parseFloat(raw);
              return Number.isFinite(n) ? n * 100 : NaN;
            }}
          />
        ))}
      </div>

      {/* Rotation */}
      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Rotation</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => {
          const deg = (rot[i] * 180) / Math.PI;
          return (
            <SliderRow
              key={`rot-${axis}`}
              label={axis}
              labelWidth={16}
              value={Math.round(deg * 10)}
              min={-1800}
              max={1800}
              step={1}
              displayValue={`${deg.toFixed(1)}°`}
              onChange={(v) => setRot(i, v / 10)}
              defaultValue={0}
              parseValue={(raw) => {
                const n = parseFloat(raw.replace("°", ""));
                return Number.isFinite(n) ? n * 10 : NaN;
              }}
            />
          );
        })}
      </div>

      {/* Scale */}
      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Scale</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => (
          <SliderRow
            key={`scl-${axis}`}
            label={axis}
            labelWidth={16}
            value={Math.round(scl[i] * 100)}
            min={1}
            max={1000}
            step={1}
            displayValue={scl[i].toFixed(2)}
            onChange={(v) => setScl(i, v / 100)}
            defaultValue={100}
            midpoint={100}
            parseValue={(raw) => {
              const n = parseFloat(raw);
              return Number.isFinite(n) ? n * 100 : NaN;
            }}
          />
        ))}
      </div>

      {/* Apply / Reset */}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <Button
          title="Apply: bake current transform into the mesh, reset transform to identity"
          onClick={() => {
            pushUndo();
            if (!isBlend) {
              useStore.getState().applyTransform(objectId);
            }
          }}
          disabled={isBlend}
          variant="secondary"
          size="xs"
          style={{ flex: 1 }}
        >
          Apply
        </Button>
        <Button
          title="Reset transform to default (position 0, rotation 0, scale 1)"
          onClick={() => {
            pushUndo();
            update([0, 0, 0], [0, 0, 0], [1, 1, 1]);
          }}
          variant="secondary"
          size="xs"
          style={{ flex: 1 }}
        >
          Reset
        </Button>
      </div>
    </>
  );

  if (bare) return transformContent;

  return (
    <CollapsiblePanel
      id="transform"
      title="Transform"
      icon={<Move3d size={11} />}
      summary={summary}
    >
      {transformContent}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Shared group transform editor — used by blend entries and collections
// ---------------------------------------------------------------------------

function GroupTransformSection({
  gt,
  bare,
  onUpdate,
  onReset,
  onApply,
}: {
  gt: Readonly<typeof IDENTITY_GROUP_TRANSFORM>;
  bare?: boolean;
  onUpdate: (
    newPos: [number, number, number],
    newRot: [number, number, number],
    newScl: [number, number, number],
  ) => void;
  onReset: () => void;
  /**
   * Optional Apply handler. When provided, an Apply button is rendered
   * alongside Reset; the handler is expected to own its own undo push
   * (see store.applyCollectionTransform).
   */
  onApply?: () => void;
}) {
  const pushUndo = useStore((s) => s.pushUndo);

  const rot = useMemo(() => {
    const e = new THREE.Euler().setFromQuaternion(
      new THREE.Quaternion(
        gt.rotation[0],
        gt.rotation[1],
        gt.rotation[2],
        gt.rotation[3],
      ),
    );
    return [e.x, e.y, e.z] as [number, number, number];
  }, [gt.rotation[0], gt.rotation[1], gt.rotation[2], gt.rotation[3]]);

  const pos = gt.position;
  const scl = gt.scale;

  const setPos = (axis: number, v: number) => {
    const p: [number, number, number] = [...pos];
    p[axis] = v;
    onUpdate(p, rot, scl);
  };
  const setRot = (axis: number, deg: number) => {
    const r: [number, number, number] = [...rot];
    r[axis] = (deg * Math.PI) / 180;
    onUpdate(pos, r, scl);
  };
  const setScl = (axis: number, v: number) => {
    const s: [number, number, number] = [...scl];
    s[axis] = v;
    onUpdate(pos, rot, s);
  };

  const summary = `${pos[0].toFixed(1)}, ${pos[1].toFixed(1)}, ${pos[2].toFixed(1)}`;

  const transformContent = (
    <>
      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Position</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => (
          <SliderRow
            key={`pos-${axis}`}
            label={axis}
            labelWidth={16}
            value={Math.round(pos[i] * 100)}
            min={-1000}
            max={1000}
            step={1}
            displayValue={pos[i].toFixed(2)}
            onChange={(v) => setPos(i, v / 100)}
            defaultValue={0}
            parseValue={(raw) => {
              const n = parseFloat(raw);
              return Number.isFinite(n) ? n * 100 : NaN;
            }}
          />
        ))}
      </div>

      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Rotation</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => {
          const deg = (rot[i] * 180) / Math.PI;
          return (
            <SliderRow
              key={`rot-${axis}`}
              label={axis}
              labelWidth={16}
              value={Math.round(deg * 10)}
              min={-1800}
              max={1800}
              step={1}
              displayValue={`${deg.toFixed(1)}°`}
              onChange={(v) => setRot(i, v / 10)}
              defaultValue={0}
              parseValue={(raw) => {
                const n = parseFloat(raw.replace("°", ""));
                return Number.isFinite(n) ? n * 10 : NaN;
              }}
            />
          );
        })}
      </div>

      <div style={{ marginBottom: 8 }}>
        <SubsectionLabel>Scale</SubsectionLabel>
        {AXIS_LABELS.map((axis, i) => (
          <SliderRow
            key={`scl-${axis}`}
            label={axis}
            labelWidth={16}
            value={Math.round(scl[i] * 100)}
            min={1}
            max={1000}
            step={1}
            displayValue={scl[i].toFixed(2)}
            onChange={(v) => setScl(i, v / 100)}
            defaultValue={100}
            midpoint={100}
            parseValue={(raw) => {
              const n = parseFloat(raw);
              return Number.isFinite(n) ? n * 100 : NaN;
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        {onApply && (
          <Button
            title="Apply: bake this transform into each member's local transform, reset to identity"
            onClick={onApply}
            variant="secondary"
            size="xs"
            style={{ flex: 1 }}
          >
            Apply
          </Button>
        )}
        <Button
          title="Reset transform to default (position 0, rotation 0, scale 1)"
          onClick={() => {
            pushUndo();
            onReset();
          }}
          variant="secondary"
          size="xs"
          style={{ flex: 1 }}
        >
          Reset
        </Button>
      </div>
    </>
  );

  if (bare) return transformContent;

  return (
    <CollapsiblePanel
      id="transform"
      title="Transform"
      icon={<Move3d size={11} />}
      summary={summary}
    >
      {transformContent}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Blend group transform section — reads/writes the groupTransform directly
// ---------------------------------------------------------------------------

export function BlendGroupTransformSection({
  entryId,
  bare,
}: {
  entryId: string;
  bare?: boolean;
}) {
  const gt =
    useStore((s) => s.blendFiles.get(entryId)?.groupTransform) ??
    IDENTITY_GROUP_TRANSFORM;
  const updateGroupTransform = useStore((s) => s.updateGroupTransform);

  const update = (
    newPos: [number, number, number],
    newRot: [number, number, number],
    newScl: [number, number, number],
  ) => {
    const q = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(newRot[0], newRot[1], newRot[2]),
    );
    updateGroupTransform(entryId, {
      position: newPos,
      rotation: [q.x, q.y, q.z, q.w],
      scale: newScl,
    });
  };

  return (
    <GroupTransformSection
      gt={gt}
      bare={bare}
      onUpdate={update}
      onReset={() => update([0, 0, 0], [0, 0, 0], [1, 1, 1])}
    />
  );
}

export function CollectionTransformSection({
  collectionId,
  bare,
}: {
  collectionId: string;
  bare?: boolean;
}) {
  const gt =
    useStore((s) => s.collections.get(collectionId)?.transform) ??
    IDENTITY_GROUP_TRANSFORM;
  const updateCollectionTransform = useStore(
    (s) => s.updateCollectionTransform,
  );
  const applyCollectionTransform = useStore((s) => s.applyCollectionTransform);

  const update = (
    newPos: [number, number, number],
    newRot: [number, number, number],
    newScl: [number, number, number],
  ) => {
    const q = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(newRot[0], newRot[1], newRot[2]),
    );
    updateCollectionTransform(collectionId, {
      position: newPos,
      rotation: [q.x, q.y, q.z, q.w],
      scale: newScl,
    });
  };

  return (
    <GroupTransformSection
      gt={gt}
      bare={bare}
      onUpdate={update}
      onReset={() => update([0, 0, 0], [0, 0, 0], [1, 1, 1])}
      onApply={() => applyCollectionTransform(collectionId)}
    />
  );
}
